import { useState, useEffect, useCallback } from 'react';
import { Star, Copy, Check, ChevronRight, ShieldAlert, Gift, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SmartKick Live Scores — "Free Bets" tab.
 *
 * An admin-managed list of UK bookmaker sign-up offers (affiliate deals).
 * Anonymous / no auth (the Scores app has none). Includes a mandatory UK
 * 18+ / BeGambleAware responsible-gambling footer.
 */

const StarRow = ({ rating = 0 }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const filled = i < full || (i === full && half);
        return (
          <Star
            key={i}
            className={`w-3.5 h-3.5 ${filled ? 'text-amber-400 fill-amber-400' : 'text-zinc-600'}`}
            strokeWidth={2}
          />
        );
      })}
      <span className="ml-1 text-[11px] font-bold text-amber-400">{Number(rating).toFixed(1)}</span>
    </div>
  );
};

const OfferCard = ({ offer, onClaim, onCopy, copiedId }) => {
  const copied = copiedId === offer.id;
  return (
    <div
      className={`relative rounded-2xl bg-[var(--sk-surface)] border ${
        offer.is_best_offer ? 'border-[var(--sk-accent-border)] shadow-accent' : 'border-[var(--sk-border)]'
      } overflow-hidden transition-transform active:scale-[0.99]`}
      data-testid={`offer-card-${offer.id}`}
    >
      {offer.is_best_offer && (
        <div className="absolute top-0 right-0 z-10">
          <div className="bg-[var(--sk-accent)] text-white text-[9px] font-black uppercase tracking-[0.12em] px-3 py-1 rounded-bl-xl">
            Best Offer
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center gap-3">
          {/* Logo */}
          <div className="shrink-0 w-14 h-14 rounded-xl bg-white/95 flex items-center justify-center overflow-hidden">
            {offer.logo_url ? (
              <img
                src={offer.logo_url}
                alt={offer.name}
                className="w-full h-full object-contain p-1.5"
                loading="lazy"
              />
            ) : (
              <span className="text-[var(--sk-bg)] font-black text-lg">{offer.name?.charAt(0)}</span>
            )}
          </div>

          {/* Name + rating */}
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-black text-white truncate">{offer.name}</div>
            <div className="mt-1"><StarRow rating={offer.rating} /></div>
          </div>
        </div>

        {/* Headline */}
        <div className="mt-3">
          <div className="text-[17px] font-black text-white leading-tight">{offer.offer_headline}</div>
          {offer.description && (
            <p className="mt-1 text-[12px] text-zinc-400 leading-snug">{offer.description}</p>
          )}
        </div>

        {/* Promo code */}
        {offer.promo_code && (
          <button
            onClick={() => onCopy(offer)}
            className="mt-3 w-full flex items-center justify-between gap-2 rounded-xl border border-dashed border-[var(--sk-accent-border)] bg-[var(--sk-accent-soft)] px-3 py-2"
            data-testid={`offer-copy-code-${offer.id}`}
          >
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Promo code</span>
            <span className="flex items-center gap-2">
              <span className="font-mono font-black text-white tracking-widest text-sm">{offer.promo_code}</span>
              {copied ? (
                <Check className="w-4 h-4 text-emerald-400" />
              ) : (
                <Copy className="w-4 h-4 text-[var(--sk-accent)]" />
              )}
            </span>
          </button>
        )}

        {/* CTA */}
        <button
          onClick={() => onClaim(offer)}
          className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--sk-accent)] hover:brightness-110 text-white font-black text-sm py-3 transition-all"
          data-testid={`offer-claim-${offer.id}`}
        >
          Claim Offer
          <ExternalLink className="w-4 h-4" />
        </button>

        {/* T&Cs */}
        {offer.terms && (
          <details className="mt-2 group">
            <summary className="list-none cursor-pointer flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-300">
              <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
              Terms &amp; Conditions
            </summary>
            <p className="mt-1.5 text-[10px] leading-relaxed text-zinc-500">{offer.terms}</p>
          </details>
        )}
      </div>
    </div>
  );
};

const ScoresOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await fetch(`${API_URL}/api/scores/bookmakers`);
      if (!r.ok) throw new Error('bad');
      const data = await r.json();
      setOffers(Array.isArray(data.offers) ? data.offers : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const handleClaim = (offer) => {
    // Best-effort click tracking — don't block the redirect on it.
    try {
      fetch(`${API_URL}/api/scores/bookmakers/${offer.id}/click`, { method: 'POST' });
    } catch { /* ignore */ }
    if (offer.cta_url) {
      window.open(offer.cta_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopy = (offer) => {
    if (!offer.promo_code) return;
    try {
      const p = navigator.clipboard?.writeText(offer.promo_code);
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch { /* ignore */ }
    setCopiedId(offer.id);
    setTimeout(() => setCopiedId((c) => (c === offer.id ? null : c)), 1800);
  };

  return (
    <div className="px-3 pt-3" data-testid="scores-offers-page">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-[var(--sk-accent)]/20 to-[var(--sk-surface)] border border-[var(--sk-accent-border)] p-4 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-[var(--sk-accent)] flex items-center justify-center">
            <Gift className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-lg font-black text-white">Free Offers</h1>
        </div>
        <p className="text-[12px] text-zinc-300 leading-snug">
          The best UK bookmaker sign-up bonuses and welcome offers — handpicked and updated regularly.
        </p>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="offers-loading">
          <div className="animate-spin rounded-full h-7 w-7 border-t-2 border-[var(--sk-accent)]" />
        </div>
      ) : error ? (
        <div className="text-center py-16 text-zinc-500 text-sm" data-testid="offers-error">
          Couldn&apos;t load offers right now.
          <button onClick={fetchOffers} className="block mx-auto mt-3 text-[var(--sk-accent)] font-bold">Retry</button>
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 text-sm" data-testid="offers-empty">
          <Gift className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
          No offers available yet — check back soon.
        </div>
      ) : (
        <div className="space-y-3" data-testid="offers-list">
          {offers.map((offer) => (
            <OfferCard
              key={offer.id}
              offer={offer}
              onClaim={handleClaim}
              onCopy={handleCopy}
              copiedId={copiedId}
            />
          ))}
        </div>
      )}

      {/* Responsible gambling footer (mandatory) */}
      <div className="mt-6 mb-4 rounded-xl border border-[var(--sk-border)] bg-[var(--sk-surface)] p-3.5" data-testid="offers-responsible-gambling">
        <div className="flex items-start gap-2.5">
          <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-[10px] leading-relaxed text-zinc-400">
            <span className="font-black text-white">18+ · Please gamble responsibly.</span>{' '}
            Offers apply to new customers only. Always read the full terms &amp; conditions. When the fun stops, stop.{' '}
            <a
              href="https://www.begambleaware.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--sk-accent)] font-bold underline"
              data-testid="begambleaware-link"
            >
              BeGambleAware.org
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoresOffers;
